import { useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '@safetyhub/ui';
import { PhotoGradeBadge } from './PhotoGradeBadge';
import { AnalysisRegionBox } from './AnalysisRegionBox';
import { toAbsoluteApiFileUrl } from '../lib/api';
import {
  deriveFindingLabel,
  deriveKnowledgeExportBadge,
  deriveRegionDisplay,
  KNOWLEDGE_EXPORT_COLORS,
  KNOWLEDGE_EXPORT_TOOLTIP,
  NO_ANALYSIS_SUMMARY_LABEL,
  PHOTO_REVIEWED_LABEL,
  purposeTabLabel,
  REGION_INVALID_LABEL,
  REGION_MARKED_LABEL,
  type AdminMlPhotoRow,
} from '../lib/ml-sources';

/**
 * ML 사진 **맥락 패널**(P4-f ⓓ) — 한 사진의 **분석 내용**을 한 화면에 모은다.
 *
 * 등급(`grade`)은 **문서화·삭제 상태**(결재 완료 / 보고서에 실림 / 삭제)를 말하고 이 패널은
 * **분석 내용이 무엇인가**를 말한다. 목록에 등급만 있어서 운영자가 **「A등급 98장이 각각 무슨
 * 내용인가」**를 알 수 없었다.
 *
 * ⚠️ 두 축 **다 성질이고 판정이 아니다** — 「학습 대상/제외」류 문구를 이 패널에 넣지 말 것.
 * 모집단 선택은 학습 단계의 일이다(근거·결정 출처는 `lib/ml-sources.ts`의
 * `ML_PHOTO_GRADE_COLORS` JSDoc).
 *
 * ## 기존 뷰어를 대체하지 않는다
 *
 * 사진 클릭의 종전 동작은 `ImageLightbox`(줌·패닝·좌우 이동)였고 그것은 **공유 컴포넌트**다
 * (`pages/Inspections.tsx`·`pages/UserFeedback.tsx`도 쓴다). 그래서 거기에 ML 전용 메타데이터를
 * 밀어넣지 않고, 패널 안의 **「원본 크게 보기」가 그 lightbox를 같은 인덱스로 띄운다** — 줌·이동
 * 기능이 그대로 남는다. 모달 틀도 새로 만들지 않고 공용 `@safetyhub/ui` `Modal`을 쓴다
 * (lightbox가 `z-[10000]`, `Modal`이 `z-[70]`이라 겹쳐 띄워도 lightbox가 위다).
 *
 * ## 「없을 때」의 규칙 — 칸마다 다르다
 *
 * | 칸 | 비었을 때 | 왜 |
 * |---|---|---|
 * | 분석 요약 · 발견사항 | **문구를 적는다**(「분석 없음」·「발견사항 없음」) | 「없다」는 것 자체가 **읽을 값**이다. 빈 칸으로 두면 「없음」과 「못 읽었음」이 같은 모양이 된다 |
 * | 그 외(위험 요약·개선 대책·위험 유형·중점 카테고리·공정·영역) | **행째로 렌더하지 않는다** | 라벨만 남기면 같은 혼동이 반대 방향으로 생긴다(계약 ⓓ) |
 * | 「내보내기 처리」·「검수 완료」 배지 | **배지 없음**(조건부 렌더) | 존재가 곧 상태를 뜻하는 요소라, 빈 배지를 그리면 그 손실이 spec에서 초록으로 지나간다 |
 *
 * ⚠️ 그래서 공용 `InfoRow`를 쓰지 않는다 — 그것은 빈 값에 `-`를 채워 **「값이 없다」와 「칸을
 * 안 그렸다」를 섞는다.** 여기서는 그 둘을 가르는 것이 요구사항이다.
 */
export function MlPhotoContextPanel({
  photo,
  onClose,
  onOpenOriginal,
  lightboxOpen = false,
}: {
  photo: AdminMlPhotoRow;
  onClose: () => void;
  /** 「원본 크게 보기」 — 호출부가 기존 `ImageLightbox`를 그 사진 인덱스로 띄운다. */
  onOpenOriginal: () => void;
  /**
   * 호출부가 띄운 lightbox가 지금 열려 있나 — 그동안 이 패널의 Esc를 끈다. lightbox는 window
   * keydown으로, base `Modal`은 document keydown으로 Esc를 받아 **둘 다** 돌므로(document가 먼저)
   * 끄지 않으면 Esc 한 번에 lightbox와 패널이 함께 닫힌다. lightbox 상태는 호출부가 들고 있어
   * 그 값을 받기만 한다(점검 상세는 상태가 하위 섹션에 있어 알림 훅을 썼다 — `Inspections`의
   * `openLightboxCount`. 가르는 것은 「누가 lightbox 상태를 들고 있나」다).
   */
  lightboxOpen?: boolean;
}) {
  const exportBadge = deriveKnowledgeExportBadge(photo);
  const regionState = deriveRegionDisplay(photo.analysisRegion);
  /**
   * 영역 오버레이의 **측정 대상** — `AnalysisRegionBox`가 이 요소를 재서 박스를 그린다.
   *
   * ⚠️ 이 자리는 상자를 이미지에 맡겨(`w-fit` + `w-auto`) **상자 비율 = 이미지 비율**이므로
   * 측정값이 종전 백분율과 **같은 좌표**를 낸다(shared spec의 「종횡비가 같은 contain」 대조군이
   * 그 동일성을 지킨다). 즉 이 배선은 동작을 바꾸지 않고, 썸네일처럼 비율이 어긋나는 자리와
   * **같은 계산 한 벌**을 쓰게 하는 것이 목적이다.
   */
  const imageRef = useRef<HTMLImageElement>(null);
  // ⚠️ 배열 칸은 `?? []`로 받는다 — 계약은 「비면 `[]`」이지만 이 화면이 계약보다 먼저 배포되면
  // `undefined`가 와서 `.length`에서 패널이 통째로 죽는다. 숫자·요약 칸과 달리 배열은 「없음」과
  // 「모름」이 **같은 결론**(행을 그리지 않는다 = 아무 주장도 하지 않는다)이라 뭉개도 거짓이 아니다.
  const riskTypes = photo.riskTypes ?? [];
  const focusCategories = photo.focusCategories ?? [];

  return (
    <Modal open onClose={onClose} title="사진 맥락" size="xl" closeOnEsc={!lightboxOpen}>
      {/* 부제(조직 · 현장) — base `Modal`에 부제 칸이 없어 본문 머리에 둔다(종전 admin 자체 base의
          `description`과 같은 자리·같은 결). */}
      <p className="mb-3 text-sm text-gray-500">{`${photo.companyName} · ${photo.siteName}`}</p>
      {/* **상단 사진(가운데) · 하단 맥락 표(전폭)** — 2026-10-07 사용자 결정
          (「기존처럼 상단에 이미지 하단에는 맥락 정보를 출력하는 쪽으로 하자」).
          그 사이 거쳐 간 **좌·우 2단은 철회됐다** — 되돌린 것은 배치 «방향»뿐이고 **원래 결함은
          고친 채로** 둔다. 종전 결함은 두 가지였고 여기서 각각 이렇게 막는다:

          ① **이미지가 좌측에 붙어 오른쪽 494px(79%)가 비었다** — 래퍼가 `inline-block`이라
             좌측 정렬이었다. ⇒ `mx-auto w-fit`으로 **가운데 정렬**한다. 세로 사진(ratio 0.45)은
             폭이 269px이라 좌우가 남는 것을 피할 수 없지만, **가운데면 균형이 잡히고** 「사진이
             길다」로 읽힌다 — 한쪽에만 몰리면 「레이아웃이 깨졌다」로 읽힌다.
          ② **가로 사진이 납작했다**(`r=2.22` → 343×155) — 범인은 좌단 상한 `md:max-w-[55%]`였다.
             ⇒ **그 상한을 뗀다.** 세로 배치에서는 가로 사진이 **624px 전폭**을 쓰므로(≈624×281,
             종전의 1.8배) 빈 폭이 아예 없다.

          ## ⚠️ 종횡비로 레이아웃을 가르지 «않는다» — 사용자가 그 교환을 숫자로 보고 택했다

          다음 사람이 반드시 둘을 묻는다 — **「왜 비율로 분기하지 않았나」**·**「왜 세로 사진이 좀
          높은가」**. 답은 측정과 결정 둘 다에 있다.

          **종횡비 census**(dev 139장 · ⚠️ **EXIF orientation 적용** — **7장이 orientation 6**이라
          미적용 시 그 7장의 가로·세로가 **뒤바뀐다**. 브라우저도 기본으로 적용하므로 같은 축이다):

          | 종횡비 | 장수 |   | 종횡비 | 장수 |
          |---|---|---|---|---|
          | `<0.57` | 9 |   | `1.05~1.20` | **1** |
          | **`0.57~1.00`** | **40** |   | `1.20~1.30` | 11 |
          | `1.00~1.05` | 6 |   | **`>=1.30`** | **72** |

          ⇒ **「항상 세로 배치」는 가로 83장을 고치고 세로·정사각 55장을 조금 높인다.** 2026-10-07
          사용자가 **그 교환을 이 숫자로 보고** 선택지 셋(비율 분기 / 항상 세로 / 항상 세로 + 높이
          더 제한) 중 **「항상 상단/하단」을 택했다** — 한 화면에 배치가 둘 섞이는 쪽을 기각한 것이다.
          즉 세로 사진이 조금 높은 것은 **놓친 것이 아니라 고른 것**이다.

          ⚠️ **되짚을 조건**: 세로가 실제로 너무 높다는 신호가 오면 처방은 **`max-h`를 낮추는 것**
          (예 `32vh`)이고 **비율 분기로 되돌리는 것이 아니다.**

          ⚠️ **CSS `flex-wrap`으로 가르는 안도 기각됐다**(측정으로). 콘텐츠 624px에 높이 상한
          `46vh`(≈600px)라 **종횡비가 0.5만 넘어도** 이미지의 「원하는 폭」이 컨테이너 절반을 넘는다.
          그래서 우단에 `min-w`를 걸면 **`0.57~1.00`의 40장도 함께 wrap되어** 전폭도 아닌 폭
          (`r=0.87` → **522×600**)으로 혼자 한 줄을 차지하고 **옆에 102px이 남는다** — 지금 고친
          결함(①)과 **같은 모양**이다. 종횡비는 **콘텐츠**라 레이아웃 질의로 분기할 수 없고, JS로
          재면 측정·전달 경로가 따라붙는다.

          ⚠️ `md:flex-row`·`min-w-0`도 함께 제거했다 — **2단이 없으니 밀어낼 칸이 없다.** 그 둘을
          남기면 없는 문제를 가리키는 포인터가 된다. 같은 이유로 2단 시절의 「768~900px 구간에서
          우단이 좁다」는 메모도 **무효**다(그 구간의 2단 자체가 사라졌다). */}
      <div className="space-y-4">
        <div className="space-y-2">
          {/* 확대 이미지 + 영역 오버레이.
              ⚠️ `relative`가 `AnalysisRegionBox`(absolute)의 기준이다 — **레이아웃을 바꿀 때마다
              이 클래스를 잃지 말 것.** 잃으면 박스가 조상 어딘가를 기준으로 떠서 「좌표가 틀렸다」
              처럼 보이는데 원인은 좌표가 아니다(그 컴포넌트 JSDoc이 전제로 적는다).
              ⚠️ 상자를 **이미지 크기에 맡긴다**(`w-fit` + 이미지가 자기 비율대로) — 상자 높이를
              고정하고 `object-contain`으로 채우면 상자와 이미지의 종횡비가 갈린다.
              ⚠️ **종전 주석의 「bbox는 %라 자동으로 맞는다 — 렌더 크기를 JS로 재지 않는다」는
              이제 틀렸다**(2026-10-07 P4-h): 박스는 `AnalysisRegionBox`가 **이미지 요소를 재서**
              그린다. 다만 이 자리는 상자 비율 = 이미지 비율이라 그 측정이 종전 백분율과 **같은
              좌표**를 내므로 동작이 바뀌지 않는다. 이 레이아웃을 바꿀 때 「%니까 괜찮다」를 근거로
              쓰지 말 것 — 근거는 **측정**이고, 측정 대상은 위 `imageRef`가 가리키는 그 이미지다.
              ⚠️ 아래 이미지에는 `object-*` 클래스가 **없다**(계산된 값은 `fill`). 지금은 상자 비율 =
              이미지 비율이라 `objectFit="contain"`과 **같은 좌표**를 내지만, `object-cover`를 붙이는
              순간 그 둘이 갈려 박스가 **조용히** 어긋난다 — **클래스와 prop은 함께 바꿀 것**
              (`MlPhotoThumbnail` JSDoc의 같은 경고와 짝이다. 2026-10-08 브라우저 실측에서 드러났다). */}
          <div className="relative mx-auto w-fit max-w-full bg-gray-100">
            <img
              ref={imageRef}
              src={toAbsoluteApiFileUrl(photo.url)}
              alt=""
              /* ⚠️ 상한을 **뷰포트 비례(vh)**로 둔다 — 요구는 「사진을 키우되 모달이 화면을 넘지
                 않게」이고, 고정 px는 짧은 화면에서 그 요구를 깬다. 46vh는 1080px 화면에서 ~497px
                 (종전 288px의 1.7배)이고 768px 화면에서도 ~353px이라 **어느 화면에서든 종전보다
                 크면서** 이미지가 뷰포트를 넘지 않는다. admin에 같은 관용의 선례가 있다
                 (`FlagDetailModal`의 `max-h-[60vh]` 등).

                 ⚠️ **세로 배치에서는 이 값이 «모달 높이»에 그대로 더해진다**(2단에서는 표와 나란히
                 놓여 더해지지 않았다). 그래서 세로 사진(r=0.45)이면 모달이 ≈**1090px**가 된다
                 (이미지 597 + 표·헤더 ≈491). 뷰포트가 그보다 짧으면 모달이 스크롤된다 — 모달 바깥
                 컨테이너가 `overflow-y-auto`라 깨지지는 않는다. **그 높이를 줄이고 싶으면 레버는
                 이 상한 하나다**(예 `34vh` → 세로 ≈440 ⇒ 모달 ≈930). 2단 시절의 780px과 비교할
                 때는 **배치가 바뀌어 높이의 뜻이 달라졌다**는 것을 함께 봐야 한다. */
              className={`block max-h-[46vh] w-auto max-w-full ${
                photo.deletedAt ? 'opacity-40 grayscale' : ''
              }`}
            />
            <AnalysisRegionBox
              region={photo.analysisRegion}
              imageRef={imageRef}
              objectFit="contain"
              weight="thick"
            />
            {photo.deletedAt && (
              <span className="absolute inset-x-0 bottom-0 bg-red-600/85 text-white text-xs py-0.5 text-center">
                삭제됨
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <PhotoGradeBadge grade={photo.grade} />
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
              {purposeTabLabel(photo.purpose)}
            </span>
            {/* 사람이 검수한 사진 — dev 33%인데 admin에는 지금까지 노출이 0건이었다 */}
            {photo.isReviewed && (
              <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">
                {PHOTO_REVIEWED_LABEL}
              </span>
            )}
            {exportBadge && (
              <span
                title={KNOWLEDGE_EXPORT_TOOLTIP}
                className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                  KNOWLEDGE_EXPORT_COLORS[exportBadge.state]
                }`}
              >
                {exportBadge.label}
              </span>
            )}
          </div>

          {/* ⚠️ 배지와 이 버튼은 **왼쪽 정렬**이다 — 이미지만 가운데(`mx-auto w-fit`)다.
              이미지를 가운데 둔 것은 **남는 폭이 한쪽에 몰리는 것**을 막기 위한 것이고(종전 결함),
              배지·버튼은 폭을 다 쓰지 않는 요소라 가운데로 띄우면 **읽는 시작점이 사진 폭에 따라
              흔들린다**(세로 사진이면 안쪽으로 들어온다). 그래서 가운데 정렬을 이미지에만 건다. */}
          <button
            type="button"
            onClick={onOpenOriginal}
            className="rounded border px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
          >
            원본 크게 보기
          </button>
        </div>

        {/* ── 하단: 맥락 표(전폭) ──────────────────────────────────────────
            전폭이라 라벨 112px를 빼고 값에 **≈500px**이 남는다(2단 시절 우단은 261px이었고 값은
            149px이었다 — 긴 문구가 그만큼 더 줄바꿈됐다). */}
        <dl className="divide-y divide-gray-100 text-sm">
          {/* 아래 둘은 **항상** 렌더한다 — 비었다는 사실이 정보다 */}
          <PanelRow label="발견사항" value={deriveFindingLabel(photo)} />
          <PanelRow
            label="분석 요약"
            value={photo.analysisSummary ?? NO_ANALYSIS_SUMMARY_LABEL}
            muted={!photo.analysisSummary}
          />

          {/* 아래는 전부 **조건부** — 값이 없으면 행 자체가 없다 */}
          {photo.riskSummary && <PanelRow label="위험 요약" value={photo.riskSummary} />}
          {riskTypes.length > 0 && (
            <PanelRow
              label="위험 유형"
              value={
                <span className="flex flex-wrap gap-1">
                  {riskTypes.map((t) => (
                    <span
                      key={t}
                      className="rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-700"
                    >
                      {t}
                    </span>
                  ))}
                </span>
              }
            />
          )}
          {photo.improvementPlan && (
            <PanelRow label="개선 대책" value={photo.improvementPlan} />
          )}
          {focusCategories.length > 0 && (
            <PanelRow label="중점 카테고리" value={focusCategories.join(', ')} />
          )}
          {photo.processType && <PanelRow label="공정" value={photo.processType} />}
          {/* `none`이면 행이 없고, `invalid`면 **못 그렸다는 사실**을 적는다(조용히 숨기지 않는다) */}
          {regionState !== 'none' && (
            <PanelRow
              label="중점 분석 영역"
              value={regionState === 'valid' ? REGION_MARKED_LABEL : REGION_INVALID_LABEL}
              muted={regionState === 'invalid'}
            />
          )}

          <PanelRow
            label="분석 이력"
            value={<AnalysisLogLink photoId={photo.id} count={photo.analysisLogCount} />}
          />
          <PanelRow
            label="점검"
            value={
              <span className="font-mono text-xs text-gray-600">
                {photo.inspectionId.slice(0, 8)}
                {photo.inspectionDeletedAt && (
                  <span className="ml-1 font-sans text-red-600">(점검 삭제)</span>
                )}
              </span>
            }
          />
        </dl>
      </div>
    </Modal>
  );
}

function PanelRow({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5 py-2 sm:flex-row sm:gap-3">
      <dt className="flex-shrink-0 text-xs font-medium text-gray-500 sm:w-28 sm:pt-0.5">
        {label}
      </dt>
      <dd className={`flex-1 whitespace-pre-wrap ${muted ? 'text-gray-400' : 'text-gray-900'}`}>
        {value}
      </dd>
    </div>
  );
}

/**
 * 분석 이력(`/ai-matching`)으로 점프 — ⓒ-3.
 *
 * ⚠️ **`count > 0`일 때만 링크다.** dev 273장 중 **78장(29%)이 분석 로그 0건**이라, 무조건 링크를
 * 그리면 그 29%가 **빈 화면**으로 간다. 0건이면 링크 대신 **「없음」을 적는다** — 아무것도 안 그리면
 * 「로그가 없다」와 「링크가 깨졌다」가 같은 모양이 된다.
 *
 * ⚠️ 게이트는 **`analysisLogCount`**(`ai_analysis_logs`)이고 `findingCount`가 아니다. 그 둘은 다른
 * 테이블을 센다 — 발견사항 기준으로 게이트를 걸면 로그 없는 사진에도 링크가 생긴다(정확히 29%).
 *
 * ⚠️ **파라미터 `photoId`는 «세 자리»가 한 짝이다 — 이름을 바꾸려면 셋을 같이 바꿔라.**
 * ① 여기(링크) ② `pages/AiMatching.tsx`의 `searchParams.get('photoId')` ③ 서버 분석 이력
 * 목록의 `photoId` 필터(`knowledge.controller.ts` → `matcher.service.ts` — Prisma 경로와 raw SQL
 * 경로 **둘 다**에 걸려 있다). 한 자리만 바꾸면 링크가 **조용히 전체 목록으로 떨어진다**
 * (빈 화면이 아니라 「좁혀지지 않은 목록」이라 더 늦게 들킨다).
 *
 * 세 자리 모두 2026-10-07에 이어졌다(확인: admin 라우트는 `App.tsx`의 `path="ai-matching"`이고
 * 받는 쪽은 그 페이지의 `useSearchParams`다 — 서버 API 경로(`/ai-analyses`)와 **SPA 경로가 다르니**
 * 헷갈리지 말 것. 이 링크가 가리키는 것은 **SPA 경로**다).
 */
function AnalysisLogLink({ photoId, count }: { photoId: string; count: number | undefined }) {
  if (typeof count !== 'number') {
    // 서버가 이 칸을 안 내리는 배포 창 — 「0건」으로 그리면 거짓이다.
    return <span className="text-gray-400">집계 없음</span>;
  }
  if (count <= 0) {
    return <span className="text-gray-400">분석 이력 없음</span>;
  }
  return (
    <Link
      to={`/ai-matching?photoId=${encodeURIComponent(photoId)}`}
      className="text-blue-600 hover:underline"
    >
      분석 이력 {count}건 보기
    </Link>
  );
}
